// v50.300 — useDailySignOff: single source of truth for the daily
// sign-off chase. Per the Dashboard Remodel 08-06 brief Part 2.
//
// Used by both:
//   • DailySignOffPin    — Band 01's first card on the dashboard
//   • GlobalSignOffBanner — persistent chase banner in the app shell
//
// Role-aware, derived (never hand-flagged):
//   • Coordinator: outstandingCount = daily_logs.status='submitted'
//                  rows the coord can see (RLS gates visibility).
//   • Partner:     outstandingCount = placements with no log for today
//                  (reuses listPlacementsMissingLog so the gate logic
//                   is identical to the Site Ops Blocked Items tab).
//
// Both sides compute:
//   • moneyUnlocked  — pence value of work still gated by sign-off
//                      (coord: sum across submitted logs of
//                       hours_worked × day_rate ÷ 10; partner:
//                       a standard 8h day × day_rate ÷ 10 estimate)
//   • windowClosesAt — when the *review window* closes for the
//                      OLDEST outstanding item. Coord = earliest
//                      submitted_at + 3 working days (the deemed-
//                      confirm window); Partner = end of today's
//                      working day (23:59 local).
//   • urgency        — calm (>8h), warning (2–8h), critical (<2h)
//   • complete       — true when nothing is outstanding today
//
// Refreshes on the same window events as SiteOpsWorkflowBand so
// confirming a log live-updates the chase.

import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/api/supabase-client";
import { useEffectiveRole } from "@/features/shell/roleOverride";
import { listPlacementsMissingLog } from "@/features/requirements/dailyLogs";

export type SignOffUrgency = "calm" | "warning" | "critical";
export type SignOffRole    = "coordinator" | "partner";

export interface DailySignOffState {
  /* True once we've completed at least one load. Used by consumers
     so they can render a stable "nothing to chase" until we know. */
  ready:              boolean;

  outstandingCount:   number;
  moneyUnlocked:      number;        // pence
  progressUnlocked:   string | null; // freeform copy ("takes Slough DC2 to 60%") — null when unknown
  windowClosesAt:     Date | null;
  role:               SignOffRole;
  urgency:            SignOffUrgency;
  complete:           boolean;

  /* Forces an immediate reload. Useful from a "I've signed it off"
     callback so the banner doesn't wait for the next interval. */
  reload:             () => void;
}

/* Standard working day in hours — used for partner-side estimate of
   moneyUnlocked when we don't yet know actual hours. */
const STANDARD_DAY_HOURS = 8;
/* Default deemed-confirm window (working days). Mirrors the server
   policy used by the Site Ops ledger; if/when this becomes a
   configurable per-coord-company setting, fetch it instead. */
const DEEMED_CONFIRM_DAYS = 3;

function todayIso(): string { return new Date().toISOString().slice(0, 10); }
function endOfWorkingDay(): Date {
  const d = new Date();
  d.setHours(23, 59, 0, 0);
  return d;
}
function addDays(d: Date, n: number): Date {
  const out = new Date(d.getTime());
  out.setDate(out.getDate() + n);
  return out;
}
function urgencyFor(windowClosesAt: Date | null): SignOffUrgency {
  if (!windowClosesAt) return "calm";
  const hoursLeft = (windowClosesAt.getTime() - Date.now()) / 3_600_000;
  if (hoursLeft < 2) return "critical";
  if (hoursLeft < 8) return "warning";
  return "calm";
}

export function useDailySignOff(): DailySignOffState {
  const role: SignOffRole = useEffectiveRole() === "partner" ? "partner" : "coordinator";

  const [ready, setReady] = useState(false);
  const [outstandingCount, setOutstandingCount] = useState(0);
  const [moneyUnlocked, setMoneyUnlocked] = useState(0);
  const [windowClosesAt, setWindowClosesAt] = useState<Date | null>(null);

  const load = useCallback(async () => {
    try {
      if (role === "coordinator") {
        /* Outstanding submitted logs awaiting this coord's confirm.
           RLS gates visibility — we only see logs against placements
           our company is the coord on. */
        const { data, error } = await supabase
          .from("daily_logs")
          .select("id, hours_worked, submitted_at, placement:placement_id!inner(agreed_day_rate)")
          .eq("status", "submitted")
          .order("submitted_at", { ascending: true })
          .limit(500);
        if (error) throw error;

        const rows = (data as unknown as Array<{
          id:            string;
          hours_worked:  number | null;
          submitted_at:  string | null;
          placement:     { agreed_day_rate: number | null } | null;
        }>) || [];

        setOutstandingCount(rows.length);

        /* moneyUnlocked = sum(hours_worked × day_rate ÷ 10). */
        const pence = rows.reduce((sum, r) => {
          const hours = Number(r.hours_worked || 0);
          const rate  = Number(r.placement?.agreed_day_rate || 0);
          if (!hours || !rate) return sum;
          return sum + Math.round((hours * rate) / 10);
        }, 0);
        setMoneyUnlocked(pence);

        /* windowClosesAt = oldest submitted_at + deemed-confirm window.
           If no submitted_at on any row, fall back to end of today. */
        const oldestSubmitted = rows
          .map(r => r.submitted_at)
          .filter((s): s is string => !!s)
          .sort()[0];
        if (oldestSubmitted) {
          setWindowClosesAt(addDays(new Date(oldestSubmitted), DEEMED_CONFIRM_DAYS));
        } else if (rows.length > 0) {
          setWindowClosesAt(endOfWorkingDay());
        } else {
          setWindowClosesAt(null);
        }
      } else {
        /* Partner: count placements with no log today. */
        const today = todayIso();
        const missing = await listPlacementsMissingLog(today);
        const count = (missing || []).length;
        setOutstandingCount(count);

        if (count > 0) {
          /* moneyUnlocked estimate: count × 8h × day_rate ÷ 10.
             We don't know actual day_rates per missing placement from
             listPlacementsMissingLog's projection, so fetch the rates
             for the placement_ids it returned. */
          const ids = missing.map(m => m.placement_id);
          const { data: rateRows } = await supabase
            .from("placements")
            .select("id, agreed_day_rate")
            .in("id", ids);
          const rates = ((rateRows || []) as Array<{ id: string; agreed_day_rate: number | null }>);
          const pence = rates.reduce((sum, p) => {
            const rate = Number(p.agreed_day_rate || 0);
            if (!rate) return sum;
            return sum + Math.round((STANDARD_DAY_HOURS * rate) / 10);
          }, 0);
          setMoneyUnlocked(pence);
          setWindowClosesAt(endOfWorkingDay());
        } else {
          setMoneyUnlocked(0);
          setWindowClosesAt(null);
        }
      }
    } catch (e) {
      console.warn("[useDailySignOff load]:", e);
      setOutstandingCount(0);
      setMoneyUnlocked(0);
      setWindowClosesAt(null);
    } finally {
      setReady(true);
    }
  }, [role]);

  useEffect(() => { void load(); }, [load]);

  /* Live-refresh on the same events SiteOpsWorkflowBand uses. */
  useEffect(() => {
    const refresh = () => { void load(); };
    window.addEventListener("lc-placements-changed", refresh);
    window.addEventListener("lc-daily-log-changed",   refresh);
    return () => {
      window.removeEventListener("lc-placements-changed", refresh);
      window.removeEventListener("lc-daily-log-changed",   refresh);
    };
  }, [load]);

  /* Tick the urgency every minute so the banner escalates calm →
     amber → red as the deadline ticks down. The base data hasn't
     changed; we just want urgencyFor(windowClosesAt) to recompute. */
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setNowTick(n => n + 1), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const urgency  = useMemo(() => urgencyFor(windowClosesAt), [windowClosesAt]);
  const complete = outstandingCount === 0;

  return {
    ready,
    outstandingCount,
    moneyUnlocked,
    progressUnlocked: null,
    windowClosesAt,
    role,
    urgency,
    complete,
    reload: () => { void load(); },
  };
}

/* Format helpers shared by the pin card + banner. */
export function formatPounds(pence: number): string {
  if (!pence) return "£0";
  const pounds = Math.round(pence / 100);
  return `£${pounds.toLocaleString("en-GB")}`;
}

export function formatTimeLeft(windowClosesAt: Date | null): string {
  if (!windowClosesAt) return "today";
  const ms = windowClosesAt.getTime() - Date.now();
  if (ms <= 0) return "now";
  const hours = Math.floor(ms / 3_600_000);
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    return `${days}d`;
  }
  if (hours >= 1) return `${hours}h`;
  const mins = Math.max(1, Math.floor(ms / 60_000));
  return `${mins}m`;
}
